import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectBoardComponent from 'webapp/components/form/select-board';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { DEFAULT_REPORT_TITLE } from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportRecord from 'webapp/models/report';
import type ReportSlideRecord from 'webapp/models/report-slide';

export default class ReportsDuplicateReportSlideComponent extends PopupInnerModalComponent<PopupData.Report.SlideDuplicate> {
	@service('request') declare requestService: Service.Request;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;

	@tracked reportRadioChoice = 1;
	@tracked selectedReportInList: number;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectedReportInList = this.reportSlide.reportId;
	}

	get reportSlide(): ReportSlide {
		return this.popupData.reportSlide;
	}

	get reportChoiceOptions() {
		return [
			{
				value: 0,
				label: 'New report',
			},
			{
				value: 1,
				label: 'Select one of the existing reports',
				disabled: !this.allReportOptions.length,
			},
		];
	}

	get isCreateNewReportChosen(): boolean {
		return this.reportRadioChoice === 0;
	}

	get firstReportOnList() {
		return this.mmReportsService.reportModels[0] ?? null;
	}

	@cached
	get allReportOptions() {
		return this.mmReportsService.normalReportModels.map((report) => {
			return {
				value: report.numericId!,
				label: report.title,
				label2: report.createdText,
				label3:
					report.id === this.reportSlide.report?.id
						? '<span class="current">Current report</span>'
						: '',
				avatar: report.image || report.fallbackImage,
				bgTheme: report.bgTheme,
				color: report.color,
				count: report.slideCount,
			};
		});
	}

	duplicateReportSlideTo(reportId: number) {
		return this.mmReportSlidesService
			.duplicate(this.reportSlide, reportId)
			.then((slideRecord: ReportSlideRecord) => {
				this.popupData.onDuplicate(slideRecord);
				this.close();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	changeSelectedReportInList(selectedReportInList: number | null) {
		this.selectedReportInList = selectedReportInList!;
	}

	@action
	reportChoiceChange(reportChoice: AnySimpleValue) {
		this.reportRadioChoice = reportChoice as number;
	}

	@action
	submit(): void {
		this.submitInProgress = true;

		let promise: Promise<void>;
		if (this.isCreateNewReportChosen) {
			const report = this.mmReportsService.createNewModel({ title: DEFAULT_REPORT_TITLE });
			promise = report
				.save()
				.then((reportRecord: ReportRecord) =>
					this.duplicateReportSlideTo(+reportRecord.id!),
				);
		} else {
			promise = this.duplicateReportSlideTo(this.selectedReportInList);
		}

		void promise.finally(() => {
			this.submitInProgress = false;
		});
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Copy" @size="lg" />
			Duplicate slide
		</div>
		<div class="content">

			<p>
				Please select the destination report for the selected slide to be duplicated to:
			</p>

			<FormRadiobuttonGroupComponent
				class="radiobutton-default"
				@name="reportChoice"
				@selected={{this.reportRadioChoice}}
				@options={{this.reportChoiceOptions}}
				@onChange={{this.reportChoiceChange}}
			/>

			<div class="dropdown">
				<FormSelectBoardComponent
					name="reports"
					@selected={{this.selectedReportInList}}
					@options={{this.allReportOptions}}
					@onChange={{this.changeSelectedReportInList}}
					@isNumeric={{true}}
					@isRequired={{true}}
					@isDisabled={{this.isCreateNewReportChosen}}
					@extraClass="cs-image"
					@dropdownSearchPlaceholder="Search"
					@showFloatlabel={{false}}
					@tooltipOnSelectionFromProperty=""
				/>
			</div>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Duplicate"
						@extraClass="save"
						@onTrigger={{this.submit}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
